import React, { useRef, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  Button,
  VStack,
  Spinner,
  Icon,
  Badge,
  Collapse,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  DownloadSimple,
  Upload,
  Circle,
  Kanban,
  CheckSquare,
  CaretDown,
  CaretRight,
} from 'phosphor-react';
import { toast } from 'sonner';
import moment from 'moment';
import 'moment/locale/pt-br';
import { useEventos, IEventoDocumento } from '../../../../hooks/useEventos';
import { IDataNoPagination } from '../../../../types/shared';

const TIPO_CONFIG: Record<string, { color: string; icon: any }> = {
  nf: { color: 'blue', icon: FileText },
  contrato: { color: 'orange', icon: Circle },
  kanban: { color: 'purple', icon: Kanban },
  checklist: { color: 'teal', icon: CheckSquare },
  outro: { color: 'gray', icon: FileText },
};

interface DocumentosTabProps {
  eventoId: number;
}

export default function DocumentosTab({ eventoId }: DocumentosTabProps) {
  const queryClient = useQueryClient();
  const { getDocumentos, uploadDocumento, downloadDocumento } = useEventos();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    evento: true,
    kanban: true,
    checklist: true,
  });

  const { data: docsData, isFetching } = useQuery<IDataNoPagination<IEventoDocumento[]>>({
    queryKey: ['evento-documentos', eventoId],
    queryFn: getDocumentos,
  });

  const uploadMut = useMutation({
    mutationFn: uploadDocumento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evento-documentos', eventoId] });
      queryClient.invalidateQueries({ queryKey: ['evento-detalhe', eventoId] });
      toast.success('Documento enviado!');
    },
    onError: () => toast.error('Erro no upload'),
  });

  const docs = docsData?.data ?? [];

  const { eventoDocs, kanbanDocs, checklistDocs } = useMemo(() => {
    const ev: IEventoDocumento[] = [];
    const kb: IEventoDocumento[] = [];
    const cl: IEventoDocumento[] = [];
    docs.forEach(d => {
      if (d.origem === 'kanban') kb.push(d);
      else if (d.origem === 'checklist') cl.push(d);
      else ev.push(d);
    });
    return { eventoDocs: ev, kanbanDocs: kb, checklistDocs: cl };
  }, [docs]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMut.mutate({ eventoId, file, tipo: 'outro' });
    e.target.value = '';
  };

  const handleDownload = async (doc: IEventoDocumento) => {
    // anexo do card e do checklist ja vem com a URL assinada; a rota e so do documento do evento
    if (doc.url) {
      window.open(doc.url, '_blank');
      return;
    }
    try {
      const res = await downloadDocumento({ eventoId, docId: doc.id });
      const url = (res as any)?.data?.url;
      if (url) window.open(url, '_blank');
    } catch {
      toast.error('Erro ao baixar documento');
    }
  };

  if (isFetching) {
    return (
      <Flex justify="center" py={8}>
        <Spinner />
      </Flex>
    );
  }

  const renderDoc = (doc: IEventoDocumento) => {
    const isKanban = doc.origem === 'kanban';
    const cfg = TIPO_CONFIG[doc.tipo] || TIPO_CONFIG.outro;
    return (
      <Flex
        key={`${doc.origem}-${doc.id}`}
        bg="white"
        p={3}
        borderRadius="md"
        border="1px solid"
        borderColor={isKanban ? 'purple.100' : 'gray.100'}
        justify="space-between"
        align="center"
      >
        <HStack spacing={3}>
          <Icon as={cfg.icon} color={`${cfg.color}.400`} boxSize={5} />
          <Box>
            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="600" color={`${cfg.color}.600`}>
                {doc.nome}
              </Text>
            </HStack>
            <HStack spacing={2} mt={0.5}>
              {isKanban && doc.card_titulo && (
                <Badge
                  fontSize="2xs"
                  colorScheme="purple"
                  variant="subtle"
                  fontWeight="500"
                  textTransform="none"
                >
                  {doc.card_titulo}
                </Badge>
              )}
              {doc.uploaded_por_nome && (
                <Text fontSize="2xs" color="gray.400">
                  {doc.uploaded_por_nome}
                </Text>
              )}
              {doc.dt_criacao && (
                <Text fontSize="2xs" color="gray.400">
                  {moment(doc.dt_criacao).format('DD/MM/YYYY')}
                </Text>
              )}
            </HStack>
          </Box>
        </HStack>
        <Button
          size="xs"
          variant="ghost"
          leftIcon={<DownloadSimple size={14} />}
          onClick={() => handleDownload(doc)}
        >
          Baixar
        </Button>
      </Flex>
    );
  };

  const totalDocs = eventoDocs.length + kanbanDocs.length + checklistDocs.length;

  const toggleSection = (key: string) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const renderSection = (
    key: string,
    label: string,
    icon: any,
    color: string,
    items: IEventoDocumento[],
  ) => {
    if (items.length === 0) return null;
    const isOpen = expanded[key] !== false;
    return (
      <Box mb={3} key={key}>
        <HStack
          spacing={2}
          mb={isOpen ? 2 : 0}
          cursor="pointer"
          onClick={() => toggleSection(key)}
          _hover={{ opacity: 0.8 }}
          userSelect="none"
        >
          <Icon as={isOpen ? CaretDown : CaretRight} boxSize={3} color={`${color}.400`} />
          <Icon as={icon} color={`${color}.400`} boxSize={4} />
          <Text fontSize="xs" fontWeight="600" color={`${color}.500`} textTransform="uppercase">
            {label}
          </Text>
          <Badge fontSize="2xs" colorScheme={color} borderRadius="full">
            {items.length}
          </Badge>
        </HStack>
        <Collapse in={isOpen} animateOpacity>
          <VStack spacing={2} align="stretch">
            {items.map(renderDoc)}
          </VStack>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      <Flex justify="flex-end" mb={3}>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Button
          size="xs"
          colorScheme="blue"
          leftIcon={<Upload size={12} />}
          onClick={() => fileInputRef.current?.click()}
          isLoading={uploadMut.isPending}
        >
          Upload
        </Button>
      </Flex>

      {renderSection('evento', 'Documentos do Evento', FileText, 'blue', eventoDocs)}
      {renderSection('kanban', 'Anexos do Kanban', Kanban, 'purple', kanbanDocs)}
      {renderSection('checklist', 'Anexos do Checklist', CheckSquare, 'teal', checklistDocs)}

      {totalDocs === 0 && (
        <Flex justify="center" py={8}>
          <Text color="gray.400" fontSize="sm">
            Nenhum documento
          </Text>
        </Flex>
      )}
    </Box>
  );
}
